import React, { useState, useEffect, useRef } from 'react';
import { Lock, Timer, AlarmClock } from 'lucide-react';
import styles from './form.module.css';

const SpeedQuizForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true, activityId = null }) => {
    const options = content?.options || [];
    const timeLimitMs = Number(content?.settings?.timeLimitMs ?? 30000);
    const startKey = `lp_speedquiz_start_${activityId}`;

    const startTime = useRef(Date.now());
    const [selected, setSelected] = useState(() => userAnswer?.answer || null);
    const [elapsed, setElapsed] = useState(0);
    const [timerExpired, setTimerExpired] = useState(false);

    useEffect(() => {
        setSelected(userAnswer?.answer || null);

        // Anchor the clock to a persisted start timestamp (survives a page reload) instead of
        // always restarting from "now" on mount — this is what responseTime (used to rank the
        // leaderboard) is measured against, so a reload was previously a straightforward way
        // to report a falsely fast time. Uses localStorage (not sessionStorage) so a NEW tab
        // opened to the same join link shares the same start time instead of getting a fresh
        // one — sessionStorage alone would let a participant dodge the clock that way.
        let start = null;
        try {
            const saved = localStorage.getItem(startKey);
            if (saved) start = Number(saved);
        } catch { /* localStorage unavailable — falls through to a fresh start below */ }
        if (!start || !Number.isFinite(start)) {
            start = Date.now();
            try { localStorage.setItem(startKey, String(start)); } catch { /* ignore */ }
        }
        startTime.current = start;

        const e = Date.now() - start;
        setElapsed(e);
        setTimerExpired(e >= timeLimitMs);
    }, [userAnswer, content, startKey, timeLimitMs]);

    useEffect(() => {
        if (userAnswer || isLocked) return;
        const interval = setInterval(() => {
            const e = Date.now() - startTime.current;
            setElapsed(e);
            if (e >= timeLimitMs) {
                setTimerExpired(true);
                clearInterval(interval);
            }
        }, 100);
        return () => clearInterval(interval);
    }, [userAnswer, isLocked, timeLimitMs]);

    const hasSubmitted = userAnswer != null;
    const isDisabled = isLocked || timerExpired || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && selected === userAnswer.answer;
    const submitDisabled = isPreview || isLocked || timerExpired || !selected || (hasSubmitted && (!allowUpdateResponse || isUnchanged));
    const submitLabel = timerExpired && !hasSubmitted ? 'Time\'s Up!' : !hasSubmitted ? (isLocked ? 'Voting Closed' : 'Submit') : (!allowUpdateResponse || isUnchanged ? 'Submitted ✓' : 'Update response');

    const handleSelect = (id) => {
        if (isDisabled) return;
        const responseTime = Date.now() - startTime.current;
        setSelected(id);
        onSubmit({ answer: id, responseTime });
    };

    const remaining = Math.max(0, timeLimitMs - elapsed);
    const pct = timeLimitMs > 0 ? (remaining / timeLimitMs) * 100 : 0;
    const timerState = pct > 50 ? 'ok' : pct > 25 ? 'warn' : 'danger';

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Answer as fast as you can!'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            {!hasSubmitted && !isLocked && (
                <div>
                    <div className={styles.speedTimerRow}>
                        <span className={styles.speedTimerLabel}><Timer size={14} aria-hidden="true" /> Time remaining</span>
                        <span className={`${styles.speedTimerValue} ${styles[timerState]}`}>
                            {Math.ceil(remaining / 1000)}s
                        </span>
                    </div>
                    <div className={styles.speedTimerTrack}>
                        <div
                            className={`${styles.speedTimerFill} ${timerState !== 'ok' ? styles[timerState] : ''}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                </div>
            )}
            {timerExpired && !hasSubmitted && (
                <div className={styles.speedTimerExpired}>
                    <AlarmClock size={20} aria-hidden="true" /> Time's up! No response recorded.
                </div>
            )}
            {options.map((opt) => {
                const id = opt.id || opt;
                const label = opt.label || opt;
                return (
                    <button
                        key={id}
                        className={`${styles.option} ${selected === id ? styles.selected : ''}`}
                        onClick={() => handleSelect(id)}
                        disabled={isDisabled}
                    >
                        <span className={`${styles.indicator} ${styles.radio} ${selected === id ? styles.checked : ''}`}>
                            {selected === id && '✓'}
                        </span>
                        {label}
                    </button>
                );
            })}
            {!timerExpired && (
                <button className={styles.submitBtn} disabled={submitDisabled} style={{ display: 'none' }}>
                    {submitLabel}
                </button>
            )}
        </div>
    );
};

export default SpeedQuizForm;
